import { Field, HStack, Slider, Stack } from "@chakra-ui/react"
import withTranslate from "@pathwright/ux/src/components/lng/withTranslate"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import { Component } from "react"
import { LICENSING_MODE_SCHOOL_LICENSING } from "../../../store/resource/offering/license/constants"
import { getMinSeats } from "../utils"

class LicenseSeatSlider extends Component {
  constructor(props) {
    super(props)

    this.tPrefix = "resource.licensing_form.seat_slider"
  }

  // NOTE: purposely not DRY since copy for each label may get updated individually.
  get sliderDescriptionLabel() {
    const { t } = this.props
    const {
      cost_per_seat,
      minimum_license_duration,
      include_creator_seat,
      include_in_subscription_plans,
      included_in_user_subscription
    } = this.props.licenseOffering
    const minSeatsLabel = this.minSeats > 1 ? `${this.minSeats - 1}+` : ""
    const minSeatsCreatorIncludedLabel = this.minSeats
      ? `${this.minSeats}+`
      : ""
    const durationLabel = minimum_license_duration
      ? t(`${this.tPrefix}.days_of`, { minimum_license_duration })
      : ""
    const seatCostLabel =
      this.props.currencyFormatter.customFormat(cost_per_seat)

    if (cost_per_seat <= 0) {
      return t(`${this.tPrefix}.reserve_access`, { durationLabel })
    }

    if (
      this.props.licenseOffering === LICENSING_MODE_SCHOOL_LICENSING ||
      !include_in_subscription_plans.length
    ) {
      if (include_creator_seat) {
        return t(`${this.tPrefix}.pre_purchase_creator_free`, {
          durationLabel,
          minSeatsCreatorIncludedLabel,
          seatCostLabel
        })
      }
      return t(`${this.tPrefix}.pre_purchase`, {
        durationLabel,
        minSeatsLabel,
        seatCostLabel
      })
    }

    if (included_in_user_subscription) {
      return t(`${this.tPrefix}.optionally_purchase`, {
        durationLabel,
        seatCostLabel
      })
    }

    if (include_creator_seat) {
      return t(`${this.tPrefix}.pre_purchase_creator_subscribers`, {
        durationLabel,
        minSeatsCreatorIncludedLabel,
        seatCostLabel
      })
    } else {
      return t(`${this.tPrefix}.pre_purchase_subscribers`, {
        durationLabel,
        minSeatsLabel,
        seatCostLabel
      })
    }
  }

  get sliderPromptLabel() {
    const { t } = this.props
    const { include_in_subscription_plans } = this.props.licenseOffering

    if (include_in_subscription_plans.length) {
      return t(`${this.tPrefix}.how_many_non_subscribed`)
    }

    return t(`${this.tPrefix}.how_many`)
  }

  get discountForSeatsLabel() {
    if (this.props.computedDiscount) {
      const { t } = this.props
      const { max_seat_discount, discount_percent } =
        this.props.computedDiscount
      const discountPercent = Math.round(discount_percent * 100)

      if (max_seat_discount) {
        return t(`${this.tPrefix}.discount_members`, {
          discountPercent,
          max_seat_discount
        })
      }

      return t(`${this.tPrefix}.discount`, { discountPercent })
    }
  }

  get beforeDiscountPriceLabel() {
    if (
      this.props.computedDiscount &&
      this.props.computedDiscount.discounted_total !== this.baseCostForSeats
    ) {
      return this.props.currencyFormatter.customFormat(this.baseCostForSeats)
    }
  }

  get afterDiscountPriceLabel() {
    const total = this.props.computedDiscount
      ? this.props.computedDiscount.discounted_total
      : this.baseCostForSeats
    return this.props.currencyFormatter.customFormat(total)
  }

  get baseCostForSeats() {
    return (
      this.seatCount * Math.max(0, this.props.licenseOffering.cost_per_seat)
    )
  }

  get seatCount() {
    return Math.max(this.props.value || 0, this.minSeats)
  }

  get minSeats() {
    return getMinSeats(this.props.licenseOffering)
  }

  render() {
    const { licenseOffering, t } = this.props
    return (
      <Field.Root size="lg">
        <Stack>
          <Field.Label>{this.sliderPromptLabel}</Field.Label>
          <Field.HelperText>{this.sliderDescriptionLabel}</Field.HelperText>
        </Stack>
        <Slider.Root
          width="full"
          value={[this.seatCount]}
          onValueChange={(value) => {
            this.props.onChange(value.value[0])
          }}
          min={this.minSeats}
          max={500}
        >
          <Slider.Control>
            <Slider.Track>
              <Slider.Range />
            </Slider.Track>
            <Slider.Thumbs />
          </Slider.Control>
        </Slider.Root>
        <HStack justify="space-between" w="full">
          <span>
            <strong>{this.seatCount}</strong>{" "}
            {t(
              `${this.tPrefix}.${pluralize(
                licenseOffering.include_in_subscription_plans.length
                  ? "non-subscriber"
                  : "member",
                this.seatCount
              )}`
            )}
            <br />
            {this.discountForSeatsLabel}
          </span>
          {this.baseCostForSeats > 0 && (
            <span>
              <span style={{ textDecoration: "line-through" }}>
                {this.beforeDiscountPriceLabel}
              </span>
              &nbsp;
              <strong>{this.afterDiscountPriceLabel}</strong>
            </span>
          )}
        </HStack>
      </Field.Root>
    )
  }
}

LicenseSeatSlider.displayName = "LicenseSeatSlider"

LicenseSeatSlider.propTypes = {
  licenseOffering: PropTypes.object.isRequired,
  licenseeSchool: PropTypes.object.isRequired,
  licensingType: PropTypes.oneOf(["school", "private_group"]),
  computedDiscount: PropTypes.shape({
    max_seat_discount: PropTypes.number,
    discount_percent: PropTypes.number,
    discount_amount: PropTypes.number,
    discounted_total: PropTypes.number
  }),
  onChangeComplete: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.number
}

export default withTranslate(LicenseSeatSlider)
